import OrderNotFoundError from '../../modules/auth/domain/OrderNotFoundError';

export const DEFAULT_OTP_LENGTH = 6;
export const DEFAULT_MAX_RESENDS = 3;
export const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;
export const PHONE_NUMBER_LENGTH = 10;
export const OTP_CHANNEL_EMAIL = 'EMAIL';
export const OTP_CHANNEL_SMS = 'SMS';
export const SERVICE_UNAVAILABLE_MESSAGE = 'No pudimos comunicarnos con el servicio en este momento. Intenta de nuevo más tarde.';

export const OTP_ERROR_NONE = 'NONE';
export const OTP_ERROR_EMAIL = 'EMAIL';
export const OTP_ERROR_PHONE = 'PHONE';
export const OTP_ERROR_ORDER_NOT_FOUND = 'ORDER_NOT_FOUND';
export const OTP_ERROR_INVALID_CODE = 'INVALID_CODE';
export const OTP_ERROR_EXPIRED_CODE = 'EXPIRED_CODE';
export const OTP_ERROR_LOCKED = 'LOCKED';
export const OTP_ERROR_RESEND_LIMIT = 'RESEND_LIMIT';
export const OTP_ERROR_GENERIC = 'GENERIC';

const EMAIL_ERROR_PATTERNS = [
  'correo electrónico con formato válido',
  'ingresa un correo electrónico',
  'correo electrónico válido',
];

const PHONE_ERROR_PATTERNS = [
  'número telefónico',
  'teléfono',
  'numero de 10 dígitos',
  'numero telefono',
];

const INVALID_CODE_PATTERNS = [
  'otp inválido',
  'código incorrecto',
  'codigo incorrecto',
  'intentos restantes',
  'credenciales inválidas',
  'credenciales invalidas',
  'invalid credentials',
  'access denied due to invalid credentials',
];

const RESEND_LIMIT_PATTERNS = [
  'límite máximo de reenvíos',
  'limite maximo de reenvios',
  'alcanzaste el límite de solicitudes de código',
  'alcanzaste el limite de solicitudes de codigo',
  'podrás solicitar uno nuevo en 24 horas',
  'podras solicitar uno nuevo en 24 horas',
];

const NETWORK_ERROR_PATTERNS = [
  'network error',
  'failed to fetch',
  'load failed',
  'timeout',
];

const TECHNICAL_ERROR_PATTERNS = [
  "can't access property",
  'cannot read properties of undefined',
  'e.response is undefined',
  '(void 0) is undefined',
];

const GENERIC_CARD_ITEMS = [
  'Revisa que el correo electrónico sea correcto.',
  'Espera unos minutos antes de intentarlo nuevamente',
  'Si el problema continúa, solicita apoyo al personal de la agencia para revisar tus datos',
];

const ORDER_NOT_FOUND_ITEMS = [
  'Sea el mismo correo que proporcionaste en la concesionaria.',
  'No hayas cambiado tu correo recientemente.',
];

const ORDER_NOT_FOUND_FOOTER = 'Si necesitas ayuda, pide al personal de la agencia que confirme tus datos.';

const includesAny = (message, patterns) => patterns.some((pattern) => message.includes(pattern));

export const toLowerCase = (value) => String(value || '').toLowerCase();

export const toDigits = (value, maxLength) => String(value || '').replace(/[^0-9]/g, '').slice(0, maxLength);

export const isValidEmailFormat = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

export const isValidPhoneFormat = (phone, phoneLength = PHONE_NUMBER_LENGTH) => (
  new RegExp(`^[0-9]{${phoneLength}}$`).test(String(phone || ''))
);

export const isTechnicalErrorMessage = (message) => {
  const normalizedMessage = toLowerCase(message);
  return includesAny(normalizedMessage, TECHNICAL_ERROR_PATTERNS);
};

export const getDisplayErrorMessage = (error) => {
  const rawMessage = String(error?.message || '').trim();

  if (!rawMessage) {
    return SERVICE_UNAVAILABLE_MESSAGE;
  }

  const normalizedMessage = toLowerCase(rawMessage);

  if (
    isTechnicalErrorMessage(normalizedMessage)
    || includesAny(normalizedMessage, NETWORK_ERROR_PATTERNS)
  ) {
    return SERVICE_UNAVAILABLE_MESSAGE;
  }

  return rawMessage;
};

export const getErrorType = (error) => {
  if (!error) {
    return OTP_ERROR_NONE;
  }

  if (error instanceof OrderNotFoundError) {
    return OTP_ERROR_ORDER_NOT_FOUND;
  }

  const message = toLowerCase(getDisplayErrorMessage(error));

  if (includesAny(message, EMAIL_ERROR_PATTERNS)) {
    return OTP_ERROR_EMAIL;
  }

  if (includesAny(message, PHONE_ERROR_PATTERNS)) {
    return OTP_ERROR_PHONE;
  }

  if (includesAny(message, INVALID_CODE_PATTERNS)) {
    return OTP_ERROR_INVALID_CODE;
  }

  if (message.includes('expiró') || message.includes('expiro')) {
    return OTP_ERROR_EXPIRED_CODE;
  }

  if (message.includes('bloqueado')) {
    return OTP_ERROR_LOCKED;
  }

  if (includesAny(message, RESEND_LIMIT_PATTERNS)) {
    return OTP_ERROR_RESEND_LIMIT;
  }

  return OTP_ERROR_GENERIC;
};

export const getRequestInlineError = (error) => {
  switch (getErrorType(error)) {
    case OTP_ERROR_EMAIL:
      return 'Ingresa un correo electrónico válido';
    case OTP_ERROR_PHONE:
      return 'Ingresa un numero de 10 dígitos';
    case OTP_ERROR_ORDER_NOT_FOUND:
      return 'No encontramos una orden asociada a este correo.';
    default:
      return '';
  }
};

export const getOtpInlineError = (error) => {
  switch (getErrorType(error)) {
    case OTP_ERROR_INVALID_CODE:
      return 'Código incorrecto';
    case OTP_ERROR_EXPIRED_CODE:
      return 'El código ha expirado';
    default:
      return '';
  }
};

export const getErrorCard = (error) => {
  switch (getErrorType(error)) {
    case OTP_ERROR_EMAIL:
      return {
        type: 'info',
        title: '',
        message: '',
        items: ['Verifica que el correo esté escrito completo, por ejemplo: nombre@dominio.com'],
      };
    case OTP_ERROR_ORDER_NOT_FOUND:
      return {
        type: 'info',
        title: 'Verifica que:',
        message: '',
        items: ORDER_NOT_FOUND_ITEMS,
        footer: ORDER_NOT_FOUND_FOOTER,
      };
    case OTP_ERROR_RESEND_LIMIT:
      return {
        type: 'warning',
        title: '',
        message: 'Alcanzaste el límite de solicitudes de código. Podrás solicitar uno nuevo en 24 horas.',
        items: [],
        footer: '',
      };
    case OTP_ERROR_LOCKED:
    case OTP_ERROR_GENERIC:
      return {
        type: 'info',
        title: 'Intenta lo siguiente:',
        message: '',
        items: GENERIC_CARD_ITEMS,
        footer: '',
      };
    default:
      return null;
  }
};

export const getOtpInstructionText = (channel) => {
  if (channel === OTP_CHANNEL_SMS) {
    return 'Ingresa el código que enviamos por SMS.';
  }

  return 'Ingresa el código que enviamos a tu correo.';
};

export const getRequestInstructionText = () => 'Recibe un código para acceder a tu cuenta.';

export const shouldShowChangeMethodLink = (error, step) => step === 'otp';

export const shouldShowResendSection = (error, step) => {
  if (step !== 'otp') {
    return false;
  }

  return getErrorType(error) !== OTP_ERROR_RESEND_LIMIT;
};
